import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { GitCompare, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/app/api";
import { Card } from "@/app/ui";
import { compactList, formatDate, levelLabel, providerInitials, typeLabel } from "@/lib/opportunities";

const keyRequirements = (opportunity) =>
  (opportunity.requirements || [])
    .map((requirement) => requirement.qualification || requirement.test_type || requirement.work_experience)
    .filter(Boolean)
    .slice(0, 3);

const startDates = (opportunity) =>
  (opportunity.intakes || []).map((intake) => `${intake.intake_label} (${formatDate(intake.start_date)})`);

export default function CompareOpportunities() {
  const [items, setItems] = useState(null);
  const [busySlug, setBusySlug] = useState("");

  useEffect(() => {
    api.get("/individual/compare-opportunities")
      .then((response) => setItems(response.data.opportunities))
      .catch(() => setItems([]));
  }, []);

  const remove = async (opportunity) => {
    setBusySlug(opportunity.slug);
    try {
      await api.delete(`/individual/opportunities/${opportunity.slug}/compare`);
      setItems((current) => current.filter((item) => item.slug !== opportunity.slug));
      toast.success("Removed from comparison.");
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setBusySlug("");
    }
  };

  const rows = [
    ["Provider", (o) => o.provider?.name],
    ["Opportunity type", (o) => typeLabel(o.opportunity_type)],
    ["Qualification", (o) => o.qualification],
    ["Level", (o) => levelLabel(o.qualification_level)],
    ["Location", (o) => o.location],
    ["Study mode", (o) => o.mode],
    ["Duration", (o) => o.duration],
    ["Fees", (o) => o.fees],
    ["Start dates", (o) => compactList(startDates(o), "To be confirmed")],
    ["Key requirements", (o) => compactList(keyRequirements(o), "Provider will confirm")],
    ["Funding", (o) => o.fees_detail?.funding_availability ? "Funding information available" : "No funding route listed"],
    ["International", (o) => o.international ? "International applicants considered" : "Check provider eligibility"],
  ];

  return (
    <div data-testid="compare-opportunities-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-ink">Compare opportunities</h1>
          <p className="mt-2 max-w-2xl text-slatey">A lightweight side-by-side view of the structured facts that matter before you apply.</p>
        </div>
        <Link to="/app/discover" data-testid="compare-discover-link" className="rounded-full bg-cobalt px-4 py-2.5 text-sm font-bold text-white">
          Add from Discover
        </Link>
      </div>

      <p className="mt-6 text-sm font-semibold text-slatey" data-testid="compare-count">
        {items ? `${items.length} selected for comparison` : "Loading comparison…"}
      </p>

      {items && items.length > 0 && (
        <Card className="mt-4 hidden overflow-hidden p-0 lg:block" data-testid="comparison-table">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] border-collapse text-left">
              <thead>
                <tr className="border-b border-slate-200 bg-surface">
                  <th className="w-48 p-5 text-xs font-bold uppercase tracking-[0.14em] text-slatey">Field</th>
                  {items.map((opportunity) => (
                    <th key={opportunity.id} className="min-w-56 p-5 align-top">
                      <div className="flex items-start gap-3">
                        {opportunity.provider?.logo ? (
                          <img src={opportunity.provider.logo} alt="" className="h-10 w-10 rounded-xl border border-slate-200 object-cover" />
                        ) : (
                          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-cobalt/10 text-xs font-bold text-cobalt">{providerInitials(opportunity.provider?.name)}</div>
                        )}
                        <div>
                          <Link to={`/opportunities/${opportunity.slug}`} data-testid={`compare-view-${opportunity.slug}`} className="font-display text-lg font-bold text-ink hover:text-cobalt">
                            {opportunity.title}
                          </Link>
                          <p className="mt-1 text-xs text-slatey">{opportunity.provider?.name}</p>
                        </div>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(([label, getValue]) => (
                  <tr key={label} className="border-b border-slate-100 last:border-0">
                    <th className="bg-surface/60 p-5 text-sm font-bold text-ink">{label}</th>
                    {items.map((opportunity) => (
                      <td key={`${opportunity.id}-${label}`} className="p-5 text-sm leading-6 text-slatey">{getValue(opportunity) || "Not specified"}</td>
                    ))}
                  </tr>
                ))}
                <tr>
                  <th className="bg-surface/60 p-5 text-sm font-bold text-ink">Actions</th>
                  {items.map((opportunity) => (
                    <td key={`${opportunity.id}-actions`} className="p-5">
                      <button onClick={() => remove(opportunity)} disabled={busySlug === opportunity.slug} data-testid={`compare-remove-${opportunity.slug}`}
                        className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm font-bold text-slatey hover:border-rose-200 hover:text-rose-600">
                        <X className="h-4 w-4" /> Remove
                      </button>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <div className="mt-4 space-y-4 lg:hidden">
        {items?.map((opportunity) => (
          <Card key={opportunity.id} data-testid={`compare-card-${opportunity.slug}`} className="p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <Link to={`/opportunities/${opportunity.slug}`} className="font-display text-xl font-bold text-ink">{opportunity.title}</Link>
                <p className="mt-1 text-sm text-slatey">{opportunity.provider?.name}</p>
              </div>
              <button onClick={() => remove(opportunity)} data-testid={`compare-mobile-remove-${opportunity.slug}`} className="rounded-full p-2 text-slatey hover:bg-surface" aria-label={`Remove ${opportunity.title}`}>
                <X className="h-5 w-5" />
              </button>
            </div>
            <dl className="mt-5 space-y-3">
              {rows.slice(1, 10).map(([label, getValue]) => (
                <div key={label}>
                  <dt className="text-xs font-bold uppercase tracking-[0.14em] text-slatey">{label}</dt>
                  <dd className="mt-1 text-sm leading-6 text-ink">{getValue(opportunity) || "Not specified"}</dd>
                </div>
              ))}
            </dl>
          </Card>
        ))}
      </div>

      {items?.length === 0 && (
        <Card data-testid="compare-empty-state" className="mt-6 text-center">
          <GitCompare className="mx-auto h-10 w-10 text-cobalt" />
          <h2 className="mt-4 font-display text-2xl font-bold text-ink">Nothing to compare yet</h2>
          <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-slatey">Add up to four opportunities from Discover to compare provider, level, fees, dates and key requirements.</p>
          <Link to="/app/discover" className="mt-5 inline-flex rounded-full bg-cobalt px-5 py-3 text-sm font-bold text-white">Discover opportunities</Link>
        </Card>
      )}
    </div>
  );
}
